import React, { useEffect, useRef, useState } from "react";
import Box from "../../components/Box";
import CALiveFilterBar, {
  DEFAULT_CA_LIVE_FILTERS,
  getCALiveApiParams,
  getCALiveFiltersSummary,
  SelectedFilters,
} from "../../components/CALiveFilterBar/CALiveFilterBar";
import useSWR from "swr";
import { fetcherWithOnlyToken } from "../../utils/fetcher";
import CaCard from "../../components/CaCard";
import styled from "styled-components";
import { customElement } from "../../components/marcel/customElement";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";
import useBreakPoint from "../../utils/useBreakPoint";
import { formatKvalue, getCurrencySuffix } from "../../utils/numberUtils";
import { useTranslation } from "react-i18next";
import MrclText from "../../components/marcel/MrclText";
import { PerformanceProvider } from "../../components/Performance/context/PerformanceContext";
import PerformanceHeader from "../../components/Performance/PerformanceHeader/PerformanceHeader";
import PerformanceContent from "../../components/Performance/PerformanceContent/PerformanceContent";

const formatUpdateTime = (importedDateTime?: string) => {
  if (!importedDateTime) {
    return "--";
  }

  const date = new Date(importedDateTime.replace(" ", "T"));

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  const dateLabel = date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
  });
  const timeLabel = date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateLabel} à ${timeLabel}`;
};

const CA_LIVE_FILTERS_STORAGE_KEY = "caLiveFilters";

const loadStoredCALiveFilters = (): SelectedFilters => {
  try {
    const stored = localStorage.getItem(CA_LIVE_FILTERS_STORAGE_KEY);
    return stored ? { ...DEFAULT_CA_LIVE_FILTERS, ...JSON.parse(stored) } : DEFAULT_CA_LIVE_FILTERS;
  } catch {
    return DEFAULT_CA_LIVE_FILTERS;
  }
};

interface BudgetGapCardProps {
  value: number | null;
}

const BudgetGapCard: React.FC<BudgetGapCardProps> = ({ value }) => {
  const { t } = useTranslation();
  const displayedValue = value == null || Number.isNaN(value)
    ? "-"
    : `${value > 0 ? "+" : ""}${formatKvalue(value, "")} ${getCurrencySuffix(value)}`;

  return (
    <BudgetGapCardContainer data-testid="budget-gap-card">
      <BudgetGapTitle>{t("commercial.ecart_budget")}</BudgetGapTitle>
      <BudgetGapValue>{displayedValue}</BudgetGapValue>
      <BudgetGapDescription>{t("ca_live.budget_gap_description")}</BudgetGapDescription>
    </BudgetGapCardContainer>
  );
};

function CALive() {
  const { t } = useTranslation();
  const { isKpiExists } = useKpiVisibility();
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const currentView = useBreakPoint();
  const isPhone = currentView === "phone";
  const isMobile = isPhone || currentView === "tablet";

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(loadStoredCALiveFilters);
  const [draftFilters, setDraftFilters] = useState<SelectedFilters>(loadStoredCALiveFilters);
  const draftFiltersRef = useRef<SelectedFilters>(draftFilters);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);


  useEffect(() => {
    localStorage.setItem(CA_LIVE_FILTERS_STORAGE_KEY, JSON.stringify(selectedFilters));
  }, [selectedFilters]);

  const caLiveFilterParams = getCALiveApiParams(selectedFilters);

  const { data: caLiveSdvData, isLoading: isSdvLoading } = useSWR(
    () => {
      if (!isKpiExists("Commercial", "Turnover")) {
        return null;
      }
      return `/ca-live/sdv/turnover${caLiveFilterParams ? `?${caLiveFilterParams}` : ""}`;
    },
    (url) => fetcherWithOnlyToken(url)
  );


  useEffect(() => {
    if (caLiveSdvData) {
      window.dispatchEvent(
        new CustomEvent("caLiveStoresCounters", {
          detail: {
            totalStores: caLiveSdvData.totalStoresCount,
            connectedStores: caLiveSdvData.connectedStoresCount,
          },
        })
      );
    }
  }, [caLiveSdvData]);

  const { data: caLiveDriveData, isLoading: isDriveLoading } = useSWR(
    () => {
      if (!isKpiExists("Commercial", "Turnover")) {
        return null;
      }
      return `/ca-live/drive/turnover${caLiveFilterParams ? `?${caLiveFilterParams}` : ""}`;
    },
    (url) => fetcherWithOnlyToken(url)
  );

  const { data: averageBasketData, isLoading: isBasketLoading } = useSWR(
    () => {
      if (!isKpiExists("Commercial", "Turnover")) {
        return null;
      }
      return `/ca-live/average-basket${caLiveFilterParams ? `?${caLiveFilterParams}` : ""}`;
    },
    (url) => fetcherWithOnlyToken(url)
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const currentTimeLabel = currentTime.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const getLiveTitle = () => {
    if (!isMobile) {
      return t("ca_live.ca_en_temps_reel");
    }
    if (selectedFilters.scope === "market") {
      return t("ca_live.ca_market_en_temps_reel");
    }
    if (selectedFilters.scope === "hyper") {
      return t("ca_live.ca_hyper_en_temps_reel");
    }
    return t("ca_live.ca_france_en_temps_reel");
  };

  const totalValue = (caLiveSdvData?.caN ?? 0) + (caLiveDriveData?.caN ?? 0);
  const totalObjective = (caLiveSdvData?.objectiveAmount ?? 0) + (caLiveDriveData?.objectiveAmount ?? 0);
  const hasBudgetGapData =
    caLiveSdvData?.caN != null &&
    caLiveSdvData?.objectiveAmount != null &&
    caLiveDriveData?.caN != null &&
    caLiveDriveData?.objectiveAmount != null;
  const budgetGap = hasBudgetGapData ? totalValue - totalObjective : null;
  const filtersSummary = getCALiveFiltersSummary(selectedFilters, t);

  const openMobileFilters = () => {
    draftFiltersRef.current = selectedFilters;
    setDraftFilters(selectedFilters);
    setIsMobileFilterOpen(true);
  };

  const closeMobileFilters = () => {
    draftFiltersRef.current = selectedFilters;
    setDraftFilters(selectedFilters);
    setIsMobileFilterOpen(false);
  };

  const updateDraftFilters = (filters: SelectedFilters) => {
    draftFiltersRef.current = filters;
    setDraftFilters(filters);
  };

  const applyMobileFilters = () => {
    setSelectedFilters(draftFiltersRef.current);
    setIsMobileFilterOpen(false);
  };

  const renderCards = () => (
    <>
      <CaCard
        title={t("ca_live.ca_sdv_title")}
        value={caLiveSdvData?.caN}
        target={caLiveSdvData?.objectiveAmount}
        rate={caLiveSdvData?.progCa}
        showInfoBull={false}
        infoBull={{
          lastUpdated: undefined,
          description: t("ca_live.ca_sdv_title"),
          loading: isSdvLoading,
          isUnavailable: !caLiveSdvData || caLiveSdvData?.caN == null,
          error: !caLiveSdvData || caLiveSdvData?.caN == null,
        }}
      />

      <CaCard
        title={t("ca_live.ca_drive_title")}
        value={caLiveDriveData?.caN}
        target={caLiveDriveData?.objectiveAmount}
        rate={caLiveDriveData?.progCa}
        showInfoBull={false}
        infoBull={{
          lastUpdated: undefined,
          description: t("ca_live.ca_drive_title"),
          loading: isDriveLoading,
          isUnavailable: !caLiveDriveData || caLiveDriveData?.caN == null,
          error: !caLiveDriveData || caLiveDriveData?.caN == null,
        }}
      />

      <BudgetGapCard value={budgetGap} />

      <CaCard
        title={t("ca_live.panier_moyen_title")}
        value={averageBasketData?.caN}
        rate={averageBasketData?.progCa}
        currency="€"
        showObjective={false}
        showInfoBull={false}
        infoBull={{
          lastUpdated: undefined,
          description: t("commercial.panier_moyen"),
          loading: isBasketLoading,
          isUnavailable: !averageBasketData || averageBasketData?.caN == null,
          error: !averageBasketData || averageBasketData?.caN == null,
        }}
      />

      <CaCard
        title={t("commercial.debits")}
        value={caLiveSdvData?.clientsN}
        rate={caLiveSdvData?.progClients}
        currency="count"
        showObjective={false}
        showInfoBull={false}
        infoBull={{
          lastUpdated: undefined,
          description: t("commercial.debits"),
          loading: isSdvLoading,
          isUnavailable: !caLiveSdvData || caLiveSdvData?.clientsN == null,
          error: !caLiveSdvData || caLiveSdvData?.clientsN == null,
        }}
      />
    </>
  );

  if (isPhone && isMobileFilterOpen) {
    return (
      <MobileFilterPage>
        <MobileFilterHeader>
          <MobileBackButton
            icon="chevron-left"
            variant="transparent"
            tone="brand"
            size="s"
            aria-label={t("ca_live.filters.back")}
            onClick={closeMobileFilters}
          />
          <MobileFilterTitle>{t("ca_live.filters.title")}</MobileFilterTitle>
        </MobileFilterHeader>

        <MobileFilterBody>
          <MobileFilterSummary>{getCALiveFiltersSummary(draftFilters, t)}</MobileFilterSummary>
          <CALiveFilterBar
            value={draftFilters}
            onFilterChange={updateDraftFilters}
            variant="panel"
          />
        </MobileFilterBody>

        <MobileFilterActions>
          <ActionButton
            variant="outlined"
            tone="brand"
            size="m"
            onClick={closeMobileFilters}
          >
            {t("ca_live.filters.cancel")}
          </ActionButton>
          <ActionButton
            variant="filled"
            tone="brand"
            size="m"
            onClick={applyMobileFilters}
          >
            {t("ca_live.filters.apply")}
          </ActionButton>
        </MobileFilterActions>
      </MobileFilterPage>
    );
  }

  return (
    <>
      <Box width="100%" display="flex" flexDirection="column" style={{ gap: 16 }}>
        {isPhone ? (
          <MobileFilterBanner>
            <MobileAppliedFilters>{filtersSummary}</MobileAppliedFilters>
            <mrcl-icon-button
              icon="filter"
              variant="filled"
              tone="brand"
              size="s"
              aria-label={t("ca_live.filters.filter")}
              onClick={openMobileFilters}
            />
          </MobileFilterBanner>
        ) : (
          <CALiveFilterBar
            value={selectedFilters}
            onFilterChange={setSelectedFilters}
          />
        )}

        <LiveSummary>
          <LiveSummaryText>
            <LiveTitle>{getLiveTitle()}</LiveTitle>
            <LiveUpdated>
              <span>Actualisation SDV le {formatUpdateTime(caLiveSdvData?.importedDateTime)}</span>
              <span>Actualisation drive le {formatUpdateTime(caLiveDriveData?.importedDateTime)}</span>
            </LiveUpdated>
          </LiveSummaryText>

          {!isMobile && (
            <LiveTotalBox data-testid="live-total-box">
              <LiveTotalLabel>Total :</LiveTotalLabel>
              <LiveTotalValue>
                {caLiveSdvData?.caN != null || caLiveDriveData?.caN != null ? (
                  `${formatKvalue(totalValue, "")} ${getCurrencySuffix(totalValue)}`
                ) : (
                  "-"
                )}
              </LiveTotalValue>
            </LiveTotalBox>
          )}

          <LiveClock>
            <LiveClockLabel>Heure</LiveClockLabel>
            <LiveClockValue>{currentTimeLabel}</LiveClockValue>
          </LiveClock>
        </LiveSummary>

        {isMobile && (
          <TotalBanner>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>
              {caLiveSdvData?.caN != null || caLiveDriveData?.caN != null ? (
                `${formatKvalue(totalValue, "")} ${getCurrencySuffix(totalValue)}`
              ) : (
                "-"
              )}
            </TotalValue>
          </TotalBanner>
        )}

        {isMobile ? (
          <MobileCardsScroll>
            {renderCards()}
          </MobileCardsScroll>
        ) : (
          <CardsGrid>
            {renderCards()}
          </CardsGrid>
        )}
      </Box>

      <PerformanceProvider isCALive={true} caLiveFilters={selectedFilters} initialView={isPhone ? "rayon" : "horaire"}>
        <PerformanceCard>
          {!isPhone && <PerformanceHeader />}
          <PerformanceContent importedDateTime={caLiveSdvData?.importedDateTime} />
        </PerformanceCard>
      </PerformanceProvider>
    </>
  );
}

export default CALive;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  grid-auto-rows: 166px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BudgetGapCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
`;

const BudgetGapTitle = styled.span`
  color: #121212;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
`;

const BudgetGapValue = styled.span`
  margin-top: 18px;
  color: #0c1021;
  font-size: 24px;
  font-weight: 700;
  line-height: 26px;
`;

const BudgetGapDescription = styled.span`
  margin-top: auto;
  color: #696969;
  font-size: 12px;
  line-height: 16px;
`;

const LiveSummary = styled.div`
  width: 100%;
  margin: -4px 0 -2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
`;

const LiveSummaryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const LiveTitle = styled.span`
  color: #121212;
  font-size: 14px;
  font-weight: 700;
`;

const LiveUpdated = styled.span`
  color: #454545;
  font-size: 10px;
  line-height: 1.2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const LiveClock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const LiveClockLabel = styled.span`
  color: #454545;
  font-size: 10px;
  line-height: 1;
`;

const LiveClockValue = styled.span`
  color: #121212;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.1;
`;

const PerformanceCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 16px;
  box-sizing: border-box;

  @media (max-width: 640px) {
    width: calc(100% + 32px);
    margin: 16px -16px 0;
    background: #ffffff;
  }
`;

const LiveTotalBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  width: 228px;
  height: 46px;
  box-sizing: border-box;
  margin-top: -2px;

  @media (max-width: 640px) {
    display: none;
  }
`;

const LiveTotalLabel = styled.span`
  color: #121212;
  font-family: Ubuntu;
  font-size: var(--font-size-body-m, 16px);
  font-weight: 700;
  line-height: 24px;
`;

const LiveTotalValue = styled.span`
  color: #121212;
  font-family: Ubuntu;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
`;

const TotalBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.05);
`;

const TotalLabel = styled.span`
  color: #121212;
  font-family: Ubuntu;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
`;

const TotalValue = styled.span`
  color: #0c1021;
  font-family: Ubuntu;
  font-size: 20px;
  font-weight: 700;
`;

const MobileCardsScroll = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 16px;
  width: 100%;
  height: 166px;
  padding: 4px 12px;
  scroll-padding: 0 12px;
  box-sizing: border-box;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  & > div {
    flex: 0 0 85%;
    scroll-snap-align: start;
    height: 100%;
  }
`;

const MobileFilterBanner = styled.div`
  width: 100%;
  margin: 12px 0 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const MobileAppliedFilters = styled(MrclText)`
  color: #0970e6;
  font-size: 14px !important;
  font-weight: 700;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileFilterPage = styled.div`
  min-height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
  background: #f7f7f7;
`;

const MobileFilterHeader = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  background: #ffffff;
  border-bottom: 1px solid #ebebeb;
  box-sizing: border-box;
`;

// The inner shadow button is sized from --mrcl-sizing-3xl (size "s"): match it
// to the space left inside the 1px border so the icon stays centred.
const MobileBackButton = styled(customElement("mrcl-icon-button"))`
  --mrcl-sizing-3xl: 30px;
  box-sizing: border-box;
  width: 32px;
  min-width: 32px;
  height: 32px;
  border: 1px solid #0970e6;
  border-radius: 4px;
`;

const MobileFilterTitle = styled(MrclText)`
  color: #121212;
  width: 47px;
  height: 23px;
  font-family: Ubuntu;
  font-size: 15px !important;
  font-weight: 700;
  line-height: 22.5px;
`;

const MobileFilterBody = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
`;

const MobileFilterSummary = styled(MrclText)`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  width: auto;
  max-width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #0970e6;
  font-size: 14px !important;
  font-weight: 700;
  line-height: 20px;
  border: 1px solid #0970e6;
  border-radius: 4px;
  padding: 0 2px;
  margin: 0;
  white-space: normal;
`;

const MobileFilterActions = styled.div`
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 12px 16px 16px;
  background: #ffffff;
  border-top: 1px solid #ebebeb;
  box-sizing: border-box;
`;

const ActionButton = styled(customElement("mrcl-button"))`
  width: 100%;
  --mrcl-sizing-5xl: 44px;
`;
